import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { RichTextEditor } from './RichTextEditor';
interface AddInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (invoice: {
    supplier: string;
    invoiceNumber: string;
    date: string;
    category: 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet';
    type: 'Ladeboks' | 'Andet';
    amount: number;
    paid: boolean;
    customerId?: string;
    customerName?: string;
    notes?: string;
  }) => void;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function AddInvoiceModal({
  isOpen,
  onClose,
  onAdd,
  customers
}: AddInvoiceModalProps) {
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState<'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet'>('Materialer');
  const [type, setType] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState('no');
  const [customerId, setCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplier.trim() || !invoiceNumber.trim() || !date || !amount) return;
    const customer = customers.find(c => c.id === customerId);
    onAdd({
      supplier,
      invoiceNumber,
      date,
      category,
      type,
      amount: parseFloat(amount),
      paid: paid === 'yes',
      customerId: customerId || undefined,
      customerName: customer?.name || undefined,
      notes: notes || undefined
    });
    // Reset form
    setSupplier('');
    setInvoiceNumber('');
    setDate('');
    setCategory('Materialer');
    setType('Ladeboks');
    setAmount('');
    setPaid('no');
    setCustomerId('');
    setNotes('');
    onClose();
  };
  return <AnimatePresence data-id="element-72" data-mp="3iqifuaigx8ha">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-73" data-mp="3mr2bf9ne841b" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-74" data-mp="5hdycb60aag8">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-75" data-mp="2dq23bcjht342">
              <div className="flex items-center justify-between mb-6" data-id="element-76" data-mp="28qpeim929lyu">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-77" data-mp="28ssfu2fnz1iv">
                  Tilføj faktura
                </h2>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-78" data-mp="1ysi1uflt1q2n">
                  <XIcon className="w-6 h-6" data-id="element-79" data-mp="2rh76uppzefjo" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-80" data-mp="2mq78dg17rpf2">
                <div data-id="element-81" data-mp="2moe3a5691d96">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-82" data-mp="2hr42xuhbnh6k">
                    Faktura information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-83" data-mp="3f7jm6lnk3eka">
                    <Input label="Leverandør" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Indtast leverandør" required data-id="element-84" data-mp="3ag1k1u9sr7xq" />

                    <Input label="Faktura nr." value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="f.eks. INV-2024-001" required data-id="element-85" data-mp="2kzlvfsxjh4mu" />

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-86" data-mp="3dqm3ceyzqsjs" />

                    <Select label="Type" value={type} onChange={e => setType(e.target.value as 'Ladeboks' | 'Andet')} options={[{
                  value: 'Ladeboks',
                  label: 'Ladeboks'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-87" data-mp="38whv7sdzoeh4" />

                    <Select label="Kategori" value={category} onChange={e => setCategory(e.target.value as 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet')} options={[{
                  value: 'Sikkerhed',
                  label: 'Sikkerhed'
                }, {
                  value: 'Værktøj',
                  label: 'Værktøj'
                }, {
                  value: 'Materialer',
                  label: 'Materialer'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-88" data-mp="1uqdamfm47pcu" />

                    <Input label="Beløb (kr)" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="f.eks. 1500" required data-id="element-89" data-mp="11b0ym6tzk9n5" />

                    <Select label="Betalt" value={paid} onChange={e => setPaid(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-90" data-mp="ilb31hiefju6" />

                    <Select label="Kunde (valgfri)" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                  value: '',
                  label: 'Ingen kunde'
                }, ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.customerId})`
                }))]} data-id="element-91" data-mp="13vqz4g9iaz7" />
                  </div>

                  <div className="mt-4" data-id="element-92" data-mp="34lw571cevcl9">
                    <RichTextEditor label="Noter" value={notes} onChange={setNotes} placeholder="Tilføj noter om denne faktura" rows={3} data-id="element-93" data-mp="wco88iiuiyzc" />
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-94" data-mp="sakwrtw6fue2">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-95" data-mp="s99ih053v48p">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-96" data-mp="4m1mx9xhb9jv">
                    Tilføj faktura
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}